import stringify from 'fast-safe-stringify'

import { SomeEntity } from '~/clients/api-guards'
import { getErrorMessageByStatusCode } from '~/hooks/tanstack-query'

import { dispatchErrorToast } from '../slices/toasterSlice'
import { DiEntityKey, useStore } from '../store'
import { AsyncFn, lookupEntity } from './utils'

/**
 * Middleware that deletes entities from the store
 * Removes the entity from the store before the call, and adding it back if the call fails.
 * This is to provide a better user experience by showing the entity as deleted immediately.
 */
const onGoingDeletions = new Map<string, Promise<void>>()

async function runDeleteCall<T extends AsyncFn>(call: T, currentKey: string, handleFailure: () => void, ...args: Parameters<T>) {
    const deletion = call(...args)

    onGoingDeletions.set(currentKey, deletion)

    let result
    try {
        result = await deletion
    } catch (error) {
        handleFailure()
        throw error
    } finally {
        onGoingDeletions.delete(currentKey)
    }

    return result
}

export function withOptimisticDeletion<T extends string | number>(call: (arg: T) => Promise<unknown>, key: DiEntityKey): (arg: T) => Promise<void> {
    return async idOrUuid => {
        const { entity: currentEntity, numericId } = lookupEntity(key, idOrUuid)

        // If entity is not in the store, we don't do anything and return a warning
        // This is in order to prevent accidental deletions
        if (!currentEntity || numericId === undefined) {
            console.warn(`Trying to delete entity with id ${idOrUuid} from ${key}, but it's not in the store`)
            return
        }

        // On error, undo the deletion and show an error
        const onFailure = (statusCode?: number) => {
            useStore.getState().di.actions.addEntities([currentEntity])
            dispatchErrorToast({ message: getErrorMessageByStatusCode(statusCode) })
        }

        // Optimistically remove the entity from the store
        useStore.getState().di.actions.removeEntity(key, numericId)

        const currentKey = `${call.name}__${idOrUuid}`
        if (onGoingDeletions.has(currentKey)) return onGoingDeletions.get(currentKey)
        const result = await runDeleteCall(call, currentKey, onFailure, idOrUuid)

        // The backend API returns 404 when the entity is already deleted, but this
        // is actually incorrect: it should return a 204. We handle this case here.
        // We should remove this workaround when the backend is fixed.
        const alreadyDeleted = result?.error?.message === 'the resource cannot be found'

        if ([200, 204].includes(result?.response?.status) || alreadyDeleted) {
            return result
        } else {
            onFailure()
            throw new Error(`Failed to delete entity with id ${idOrUuid} from ${key}`)
        }
    }
}

export function withOptimisticBatchDeletion<T extends AsyncFn>(call: T, key: DiEntityKey): (...args: Parameters<T>) => Promise<void> {
    return async (...args: Parameters<T>) => {
        const rawIds = args[0]
        if (!Array.isArray(rawIds)) {
            console.error('withOptimisticBatchDeletion expects an array of ids as the first argument')
            return
        }

        const idsOrUuids = rawIds.filter((v): v is number | string => typeof v === 'number' || typeof v === 'string')

        if (idsOrUuids.length !== rawIds.length) {
            console.error('withOptimisticBatchDeletion expects every batch identifier to be a number or string')
            return
        }

        if (idsOrUuids.length === 0) {
            return
        }

        const resolved = idsOrUuids.map(idOrUuid => lookupEntity(key, idOrUuid))
        const currentEntities = resolved.map(r => r.entity)
        const numericIds = resolved.map(r => r.numericId).filter((id): id is number => typeof id === 'number')

        // If entity is not in the store, we don't do anything and return a warning
        // This is in order to prevent accidental deletions
        if (currentEntities.some(entity => !entity) || numericIds.length !== idsOrUuids.length) {
            console.warn(`Trying to delete entities with ids ${idsOrUuids} from ${key}, but some are not in the store`)
            return
        }

        // On error, undo the deletion and show an error
        const onFailure = (statusCode?: number) => {
            useStore.getState().di.actions.addEntities(currentEntities.filter(Boolean) as SomeEntity[])
            dispatchErrorToast({ message: getErrorMessageByStatusCode(statusCode) })
        }

        // Optimistically remove the entity from the store
        useStore.getState().di.actions.removeEntities(key, numericIds)

        const currentKey = `${call.name}__${stringify(idsOrUuids)}`
        if (onGoingDeletions.has(currentKey)) return onGoingDeletions.get(currentKey)
        const result = await runDeleteCall(call, currentKey, onFailure, ...args)
        const alreadyDeleted = result?.error?.message === 'the resource cannot be found'

        if ([200, 204].includes(result?.response?.status) || alreadyDeleted) {
            return result
        } else {
            onFailure(result?.response?.status)
            throw new Error(`Failed to delete entities with ids ${idsOrUuids} from ${key}`)
        }
    }
}
